/** @jsxImportSource @emotion/react */

import _ from 'lodash'
import { CSSObject } from '@emotion/react'
import { differenceInMilliseconds } from 'date-fns'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import React, { useEffect, useState, useCallback, useRef } from 'react'

import { trackEvent } from 'utils/useGTM'
import useDocumentEvents from 'util/useDocumentEvents'
import { normalizeAngle, toDegrees } from 'util/angleOpetations'
import { useProvider, INITIAL_DIAL_ANGLE } from 'hooks/useMoodPlot'

const rootStyles: CSSObject = {
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
}

const getRotationFromCoords = ({ x, y }: { x: number; y: number }, rect: ClientRect) => {
  const cx = rect.left + rect.width / 2
  const cy = rect.top + rect.height / 2

  return toDegrees(Math.atan2(y - cy, x - cx)) + INITIAL_DIAL_ANGLE // Add the initial dial angle so there is no offset
}

const getPoint = (e: React.TouchEvent | React.MouseEvent) => {
  if ((e as React.TouchEvent).targetTouches) {
    return (e as React.TouchEvent).targetTouches[0]
  }
  return e as React.MouseEvent
}

type RotatorProps = {
  children?: React.ReactNode
}

export default function Rotator({ children }: RotatorProps) {
  const ref = useRef<HTMLDivElement>(null)
  const {
    state: { angle, angleChangedFromWordSelection },
    setAngle,
  } = useProvider()
  // const { clearSelectedWord } = useAutocompleteSelectedWordProvider()

  // Internal variables that should not trigger a render when changed. Hence the useRef instead of useState.
  const mouseAngle = useRef<{ last: number | null }>({ last: null })
  const mouseDownStart = useRef<Date | null>(null)
  const wheelScroll = useRef<{ start: Date | null; end: Date | null }>({ start: null, end: null })
  const wheelEndTimeout = useRef<NodeJS.Timeout | null>(null)
  // End of internal variables.

  const [dragging, setDragging] = useState(false)
  const [internalAngle, setInternalAngle] = useState<number>(angle ?? 0)

  useEffect(() => {
    // Take into account only when the change to angle is coming from an emotion word selection
    if (angle === null) {
      setInternalAngle(INITIAL_DIAL_ANGLE)
    } else if (angleChangedFromWordSelection) {
      setInternalAngle(normalizeAngle(angle))
    }
  }, [angle, angleChangedFromWordSelection])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const throttledOnAngleChange = useCallback(
    _.throttle((a: number) => {
      // disable last selected word from EmotionsAutocomplete when rotating

      setAngle(normalizeAngle(a))
      // clearSelectedWord()
    }, 250),
    [],
  )

  const handleDragStart = useCallback(() => {
    mouseDownStart.current = new Date()
    setDragging(true)
  }, [])

  const handleDragStop = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (dragging) {
        const duration =
          differenceInMilliseconds(new Date(), mouseDownStart.current ?? new Date()) / 1000
        setDragging(false)
        // We need to distinguish between clicks and drags
        if (duration < 0.25) {
          const point = getPoint(e)
          if (point && ref.current) {
            // This check fixes an undefined that appears on 2nd click on mobile devices :shrug:
            const newMouseAngle = getRotationFromCoords(
              { x: point.clientX, y: point.clientY },
              ref.current.getBoundingClientRect(),
            )
            const newAngle = normalizeAngle(newMouseAngle)
            setInternalAngle(newAngle)
            throttledOnAngleChange(newAngle)
            trackEvent({ event: 'DialPlotClick' })
          }
        } else {
          trackEvent({ event: 'DialPlotDrag', duration })
        }
        // Whenever drag stops we need to unset the last mouse angle.
        mouseAngle.current.last = null
      }
    },
    [dragging, throttledOnAngleChange],
  )

  const handleRotate = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (dragging && ref.current) {
        e.preventDefault()
        e.stopPropagation()
        const point = getPoint(e)

        const newMouseAngle = getRotationFromCoords(
          { x: point.clientX, y: point.clientY },
          ref.current.getBoundingClientRect(),
        )

        if (mouseAngle.current.last === null) {
          mouseAngle.current.last = newMouseAngle
        }

        // The last angle is used so the knob is not "teleported" under the mouse when drag starts.
        const newAngle = normalizeAngle(newMouseAngle + internalAngle - mouseAngle.current.last)
        // If we need to "teleport" the knob under the mouse on drag start use:
        // const newAngle = normalizeAngle(newMouseAngle)

        setInternalAngle(newAngle)
        throttledOnAngleChange(newAngle)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dragging, throttledOnAngleChange],
  )

  // Disable body drag on mobile
  useEffect(() => {
    if (!ref.current) return
    if (dragging) {
      disableBodyScroll(ref.current, { reserveScrollBarGap: true })
    } else {
      enableBodyScroll(ref.current)
    }
  }, [dragging])

  // Attaching document event handlers because the drag can continue even outside of the component.
  useDocumentEvents(['touchmove', 'mousemove'], handleRotate)
  useDocumentEvents(['touchend', 'touchcancel', 'mouseup', 'mouseleave'], handleDragStop)

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      const newAngle = internalAngle + (e.deltaY > 0 ? 3 : -3)
      setInternalAngle(newAngle)
      throttledOnAngleChange(normalizeAngle(newAngle))

      // Check if we are currently wheel scrolling.
      if (wheelEndTimeout.current) {
        clearTimeout(wheelEndTimeout.current)
        wheelScroll.current.end = new Date()
      } else {
        // Start wheel scroll.
        wheelScroll.current.start = new Date()
        wheelScroll.current.end = new Date() // set end in case there is only a single event
      }

      wheelEndTimeout.current = setTimeout(() => {
        const duration =
          differenceInMilliseconds(
            wheelScroll.current.end ?? new Date(),
            wheelScroll.current.start ?? new Date(),
          ) / 1000
        trackEvent({ event: 'DialPlotMouseWheel', duration })
        wheelEndTimeout.current = null
      }, 750)
    },
    [internalAngle, throttledOnAngleChange],
  )

  return (
    <div
      ref={ref}
      onTouchStart={handleDragStart}
      onMouseDown={handleDragStart}
      onWheel={handleWheel}
      css={rootStyles}
      style={{ transform: `rotate(${internalAngle}deg)` }}>
      {children}
    </div>
  )
}
